import React, { useState, useCallback, useEffect } from "react";
import CustomTable from "@/components/common/table/CustomTable";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import EditIcon from "@styles/all-images/icons/edit-icon-blue.svg";
import SendIcon from "@styles/all-images/icons/send.svg";
import DeleteIcon from "@styles/all-images/icons/deleteblue.svg";
import RevertIcon from "@styles/all-images/icons/revert.svg";
import ReUploadIcon from "@styles/all-images/icons/uploadblue.svg";
import HistoryIcon from "@styles/all-images/icons/timeblue.svg";
import { NO_DATA_AVAILABLE } from "@/constants/constant-values";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import dateHandler from "@/composables/dateUtil";
import AddIMDocument from "./component/AddIMDocument";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import { toast } from "react-toastify";
import {
  IMTSDocumentApprovalStatus,
  ImTsDocumentStatus,
} from "@/constants/enums/im-ts-document-status.enum";
import StatusChips, {
  StatusChipEnumType,
} from "@/components/common/StatusChips";
import { useNavigate } from "react-router-dom";
import { DocumentType } from "../../DocumentView/components/DocumentDetails";
import FileUploadModal from "@/components/common/FileUploadModal";
import { AcceptedFileTypes } from "@/components/common/inputs/FileUpload";
import { usePermissionState } from "@/providers/PermissionProvider";
import { DocumentsPermissions } from "@/constants/permission-modules/base-permissions";
interface IMDocumentTableT3Props {
  tabId: number;
}

interface IMDocument {
  id: string;
  document_title: string;
  file_name: string;
  s3_key: string;
  version_id: string;
  size: number;
  user_id: string;
  funder_id: string;
  recipient_type: "funders" | "insurance";
  // ... add other fields as needed
}

interface DocumentData {
  id: string;
  superTrader: string;
  documentTitle: string;
  recipient: "funders" | "insurance";
  selectedProvider: string;
  file_name: string;
  s3_key: string;
  version_id: string;
  size: number;
  selectedDocument: {
    file_name: string;
    s3_key: string;
    version_id: string;
  };
}

const IMDocumentTableT3: React.FC<IMDocumentTableT3Props> = ({ tabId }) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const { formatDateWithTime } = dateHandler();
  const navigate = useNavigate();
  const [forceReset, setForceReset] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [userType, setUserType] = useState<UserTierType>(UserTierType.Tier1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadedModalOpen, setIsUploadedModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [selectedDocument, setSelectedDocument] = useState<IMDocument | null>(
    null,
  );
  const [mode, setMode] = useState<"create" | "upload" | "edit">("create");
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [itemToRevert, setItemToRevert] = useState<any>(null);
  const { isPermitted } = usePermissionState();

  const handleView = useCallback((doc: any) => {
    navigate(`/view-im-ts-document/${doc.id}?type=im&history=false`);
  }, []);

  const handleEdit = useCallback(
    async (doc: any) => {
      setMode("edit");
      setIsModalOpen(true);
      try {
        const { authenticatedCall } = actionsHandler("imDocumentListById");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customParams: [doc.id], // Append document ID to the URL
          }),
        );

        if (response?.ok) {
          setSelectedDocument(response.data.data as any);
        } else {
          throw new Error("Failed to fetch document details");
        }
      } catch (error) {
        console.error("Error fetching document details:", error);
      }
    },
    [dispatch],
  );

  const handleSend = useCallback(async (doc: any) => {
    // Implement send functionality
    try {
      const { authenticatedCall } = actionsHandler("sendIMDocument");

      const response = await dispatch(
        authenticatedCall({ method: "put", customParams: [doc.id] }),
      );
      if (response.ok) {
        setForceRefresh(true);
        toast.success(response.data.message);
      }
      return response;
    } catch (error) {
      console.error("Error fetching IM documents:", error);
      return {
        data: {
          data: [],
          meta: { count: 0, page: 1, total_pages: 1 },
        },
      };
    }
  }, []);

  const handleDelete = useCallback((item: any) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    try {
      const { authenticatedCall } = actionsHandler("imDocumentList");

      const response = await dispatch(
        authenticatedCall({
          method: "delete",
          customParams: [itemToDelete.id + "/"],
        }),
      );

      if (response.ok) {
        toast.success(response.data.message || "Deleted successfully");
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    }
  }, [dispatch, itemToDelete]);

  const handleRevert = useCallback((doc: any) => {
    setItemToRevert(doc);
    setShowRevertConfirm(true);
  }, []);

  const confirmRevert = useCallback(async () => {
    try {
      const { authenticatedCall } = actionsHandler("revertIMDocument");

      const response = await dispatch(
        authenticatedCall({
          method: "put",
          payload: { doc_id: itemToRevert.id },
          customParams: [itemToRevert.id],
        }),
      );
      if (response.ok) {
        toast.success(response.data.message);
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setShowRevertConfirm(false);
      setItemToRevert(null);
    }
  }, [dispatch, itemToRevert]);

  const handleReUpload = useCallback((doc: any) => {
    setSelectedDocument(doc);
    setMode("upload");
    setIsUploadedModalOpen(true);
  }, []);

  const handleHistory = useCallback(
    async (doc: any) => {
      navigate(`/document/im-document/history/${doc.id}/${DocumentType.IM}`);
    },
    [dispatch],
  );

  const documentColumns = [
    {
      header: "Document Name",
      accessor: (row: any) => row.document_title || NO_DATA_AVAILABLE,
      sortable: true,
      sortKey: "document_title",
    },
    {
      header: "Associate User Name",
      accessor: "associate_user_name",
      sortable: true,
      sortKey: "associate_user_name",
    },
    {
      header: "Created on Date & Time",
      accessor: (row: any) => formatDateWithTime(row.created_on),
      sortable: true,
      sortKey: "created_on",
    },
    {
      header: "Sent on Date & Time",
      accessor: (row: any) =>
        row.sent_on ? formatDateWithTime(row.sent_on) : NO_DATA_AVAILABLE,
      sortable: true,
      sortKey: "sent_on",
    },
    {
      header: "Document Status",
      accessor: (row: any) => (
        <StatusChips
          status={row.document_status}
          type={StatusChipEnumType.IMTSDocument}
          showRejectionNote={true}
          rejectionNote={row.note}
        />
      ),
      sortable: true,
      sortKey: "document_status",
    },
    {
      header: "Approval Status",
      accessor: (row: any) =>
        row.document_status === ImTsDocumentStatus.Sent ? (
          <StatusChips
            status={row.approval_status}
            type={StatusChipEnumType.IMTSDocumentApproval}
            showRejectionNote={true}
            rejectionNote={row.note}
          />
        ) : (
          NO_DATA_AVAILABLE
        ),
      sortable: true,
      sortKey: "approval_status",
    },
    {
      header: "Actions",
      accessor: (row: any) => (
        <div className="tw-flex tw-items-center">
          <button onClick={() => handleView(row)} title="View">
            <img
              src={EyeIcon}
              alt="View"
              className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
            />
          </button>
          {row.document_status === ImTsDocumentStatus.NotSent && (
            <>
              {isPermitted(DocumentsPermissions.SEND_IM_DOCUMENTS) && (
                <button onClick={() => handleSend(row)} title="Send">
                  <img
                    src={SendIcon}
                    alt="Send"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              )}
              {isPermitted(DocumentsPermissions.UPDATE_IM_DOCUMENTS) && (
                <button onClick={() => handleEdit(row)} title="Edit">
                  <img
                    src={EditIcon}
                    alt="Edit"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              )}
              {isPermitted(DocumentsPermissions.DELETE_IM_DOCUMENTS) && (
                <button onClick={() => handleDelete(row)} title="Delete">
                  <img
                    src={DeleteIcon}
                    alt="Delete"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              )}
            </>
          )}
          {row.document_status === ImTsDocumentStatus.Sent &&
            row.approval_status === IMTSDocumentApprovalStatus.Pending &&
            isPermitted(DocumentsPermissions.UPDATE_IM_DOCUMENTS) && (
              <button onClick={() => handleRevert(row)} title="Revoke">
                <img
                  src={RevertIcon}
                  alt="Revert"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
            )}
          {row.document_status === ImTsDocumentStatus.Sent &&
            row.approval_status === IMTSDocumentApprovalStatus.Rejected &&
            isPermitted(DocumentsPermissions.UPDATE_IM_DOCUMENTS) && (
              <button onClick={() => handleReUpload(row)} title="Re-upload">
                <img
                  src={ReUploadIcon}
                  alt="Re-upload"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
            )}
          {row.approval_status === IMTSDocumentApprovalStatus.Rejected &&
            row.is_history_available && (
              <button onClick={() => handleHistory(row)} title="History">
                <img
                  src={HistoryIcon}
                  alt="History"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
            )}
        </div>
      ),
    },
  ];

  const userColumns = [
    {
      header: "User Name (T4)",
      accessor: "name",
      sortable: true,
      sortKey: "name",
    },
    {
      header: "Company Name",
      accessor: (row: any) => row.company || NO_DATA_AVAILABLE,
      sortable: true,
      sortKey: "company",
    },
  ];

  const fetchDocumentsWrapper = useCallback(
    async (params: any) => {
      try {
        const { authenticatedCall } = actionsHandler("imDocumentList");

        const queryParams = new URLSearchParams({
          limit: params.limit || 10,
          page: params.page || 1,
          search: params.search || "",
          ...(params.sortColumn && {
            ordering:
              (params.sortDirection === "desc" ? "-" : "") + params.sortColumn,
          }),
        }).toString();

        const response = await dispatch(
          authenticatedCall({ method: "get", customQuery: queryParams }),
        );
        return response;
      } catch (error) {
        console.error("Error fetching IM documents:", error);
        return {
          data: {
            data: [],
            meta: { count: 0, page: 1, total_pages: 1 },
          },
        };
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const currentUserType = (user.user_type?.id ||
      UserTierType.Tier1) as UserTierType;
    setUserType(currentUserType);
    setForceRefresh(true);
  }, []);

  const renderExpandedRow = (row: any) => (
    <div className="tw-p-4">
      <CustomTable
        columns={documentColumns}
        data={row.documents}
        isStatic={true}
        fetchData={() =>
          Promise.resolve({
            data: {
              data: [],
              meta: { count: 0, page: 1, total_pages: 1, limit: 10 },
            },
          })
        }
        noDataMessage="No documents found"
        noSearchDataMessage="No matching documents found"
        hideControls={true}
        hidePagination={true}
        removeInitialPadding={true}
        forceRefresh={false}
        setForceRefresh={() => {}}
      />
    </div>
  );

  return (
    <>
      <CustomTable
        columns={userColumns}
        fetchData={fetchDocumentsWrapper}
        forceRefresh={forceRefresh}
        addButton={
          isPermitted(DocumentsPermissions.CREATE_IM_DOCUMENTS)
            ? {
                text: "Add IM Document",
                icon: PlusIcon,
                onClick: () => {
                  setIsModalOpen(true);
                },
              }
            : undefined
        }
        setForceRefresh={setForceRefresh}
        forceReset={forceReset}
        setForceReset={setForceReset}
        expandableRows
        renderExpandedRow={renderExpandedRow}
        noDataMessage="No users found"
        noSearchDataMessage="No matching users found"
      />

      {isModalOpen && (
        <AddIMDocument
          onClose={() => {
            setIsModalOpen(false);
            setSelectedDocument(null);
            setMode("create");
          }}
          onSubmit={() => {
            setForceRefresh((prev) => !prev);
            setIsModalOpen(false);
            setSelectedDocument(null);
          }}
          isModalOpen={isModalOpen}
          folderId={tabId}
          initialData={selectedDocument}
          mode={mode}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-w-[400px]">
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
              <div></div>
              <h2 className="tw-text-base tw-font-semibold">Confirm Delete</h2>
              <button onClick={() => setShowDeleteConfirm(false)}>
                <svg
                  className="tw-w-5 tw-h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="tw-p-4 tw-text-center tw-flex tw-justify-center tw-gap-2 tw-py-5 tw-px-6">
              <h6 className="tw-text-[#232323] tw-text-base tw-font-semibold tw-gap-1">
                Are you sure you want to delete this ?
                <span className="tw-inline-flex tw-items-center tw-justify-center"></span>
              </h6>
            </div>

            <div className="tw-flex tw-space-x-4 tw-py-5 tw-px-12">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revert Confirmation Modal */}
      {showRevertConfirm && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-w-[400px]">
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
              <div></div>
              <h2 className="tw-text-base tw-font-semibold">Confirm Revoke</h2>
              <button onClick={() => setShowRevertConfirm(false)}>
                <svg
                  className="tw-w-5 tw-h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="tw-p-4 tw-text-center tw-flex tw-justify-center tw-gap-2 tw-py-5 tw-px-6">
              <h6 className="tw-text-[#232323] tw-text-base tw-font-semibold tw-gap-1">
                Are you sure you want to revoke this document?
              </h6>
            </div>

            <div className="tw-flex tw-space-x-4 tw-py-5 tw-px-12">
              <button
                onClick={() => setShowRevertConfirm(false)}
                className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
              >
                Cancel
              </button>
              <button
                onClick={confirmRevert}
                className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {isUploadedModalOpen && selectedDocument?.id && (
        <FileUploadModal
          open={isUploadedModalOpen}
          title="File Upload"
          onClose={() => {
            setIsUploadedModalOpen(false);
            setSelectedDocument(null);
          }}
          maxFileSize={250 * 1000 * 1000}
          acceptedFileTypes={[AcceptedFileTypes.PDF]}
          endpoint="reuploadImDocument"
          folderId={tabId}
          isUpdateId={Number(selectedDocument?.id)}
          onSuccess={() => {
            setForceRefresh(true);
            setIsUploadedModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default IMDocumentTableT3;
